import { FC, useState, useEffect, useRef, useMemo } from 'react';
import { ProUpload } from '../../antd';
import { PlusOutlined } from '@ant-design/icons';
import Helper from '../../Helper';
import { minio_upload_api } from '../../api/oss/file';
import { getFileList } from '../utils';
import { getThumbUrl } from '../../utils/dataUtils';
import { UploadFile } from 'antd/lib/upload/interface';
import { SUCCESS_STATUS_CODE } from '../../constants/constant';
import { MinioUploadImagesProps } from '../interface';
import classNames from 'classnames';

const MinioUploadImages: FC<MinioUploadImagesProps> = props => {
  const {
    scene,
    uploadUrl,
    uploadText,
    uploadIcon,
    maxCount,
    disabled,
    fileList,
    showUploadList,
    className,
    size,
    onChange,
    onReset,
    getPopupContainer,
    ...resProps
  } = props;
  const [currentFileList, setCurrentFileList] = useState<UploadFile<any>[]>();
  const formatList = useRef<{ params: FormData; file: any }[]>([]);
  const timer = useRef<any>();
  useEffect(() => {
    if (fileList)
      setCurrentFileList(Array.isArray(fileList) ? fileList : undefined);
  }, [fileList]);
  const handleCustomRequest = async (nextFileList?: UploadFile<any>[]) => {
    if (!uploadUrl) return;
    if (formatList.current.length) {
      const file = formatList.current[0].file;
      setCurrentFileList(
        getFileList(nextFileList, file, { status: 'uploading' }),
      );
      const res = await minio_upload_api(
        uploadUrl,
        formatList.current[0].params,
      );
      if (res.code === SUCCESS_STATUS_CODE) {
        const resData = res.data;
        const newFileList = getFileList(nextFileList, file, {
          ...resData,
          status: 'done',
          thumbUrl: getThumbUrl(resData.url),
          name: formatList.current[0].file?.name,
          size: formatList.current[0].file?.size,
        });
        setCurrentFileList(newFileList);
        if (showUploadList === false) {
          onChange?.({
            ...resData,
            name: formatList.current[0].file?.name,
            size: formatList.current[0].file?.size,
          });
        } else {
          onChange?.(newFileList);
        }
        formatList.current.shift();
        if (formatList.current.length) handleCustomRequest(newFileList);
        return;
      }
      setCurrentFileList(
        getFileList(currentFileList, file, { status: 'error' }),
      );
    }
  };
  /** 点击文件预览 */
  const handlePreview = (file: UploadFile<any>) => {
    if (Array.isArray(currentFileList)) {
      Helper.previewImage({
        getPopupContainer,
        current: file.url,
        images: currentFileList.map(item => item.url || ''),
      });
    }

    return false;
  };
  const uploadButton = disabled ? null : (
    <div>
      {uploadIcon}
      <div>{uploadText}</div>
    </div>
  );
  const uploadClassName = useMemo(() => {
    return classNames(className, {
      'yb-upload-small': size === 'small',
    });
  }, [size, className]);
  
  return (
    <ProUpload
      name="file"
      listType="picture-card"
      fileList={currentFileList}
      className={uploadClassName}
      disabled={disabled}
      multiple={true}
      showUploadList={showUploadList}
      onPreview={handlePreview}
      customRequest={async (currentParams: any) => {
        if (!uploadUrl) {
          await onReset?.();
        }
        const { data, filename, file } = currentParams;
        const params = new FormData();
        const dataParams = {
          type: scene,
          compress: true,
          width: 100,
          height: 100,
          ...data,
        };
        params.set(filename, file);
        if (dataParams) {
          for (let [key, value] of Object.entries(dataParams)) {
            params.set(key, value as string);
          }
        }
        formatList.current = [...formatList.current, { params, file }];
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          handleCustomRequest(currentFileList);
        }, 200);
      }}
      onRemove={file => {
        const newFileList = currentFileList?.filter(v => v.uid !== file.uid);
        setCurrentFileList(newFileList);
        onChange?.(
          Array.isArray(newFileList) && newFileList.length
            ? newFileList
            : undefined,
        );
      }}
      {...resProps}
    >
      {(maxCount && currentFileList && currentFileList.length >= maxCount) ||
      disabled
        ? null
        : uploadButton}
    </ProUpload>
  );
};

MinioUploadImages.defaultProps = {
  uploadText: '点击上传',
  uploadIcon: <PlusOutlined />,
};

export default MinioUploadImages;
